/**
 * Sessions Report Repository
 * Data access layer for charging sessions
 */

import { SessionsReportApi, OdataResponse } from './sessions-report-api';
import { ChargingSessionsRequest, ChargingSessionsResponse, SessionBreakdownResponse, TariffLevelsMappingResponse, ChargeSessionApiModel } from '../types/api-types';
import type { SessionFilters } from '../types/session-types';
import type { MeterValueDetailApiModel, MeterValuesRequest } from '../types/meter-value-types';

export class SessionsReportRepository {
  constructor(private readonly api: SessionsReportApi) {}

  /**
   * Get charging sessions with optional filtering
   */
  async getChargingSessions(params: ChargingSessionsRequest = {}): Promise<ChargingSessionsResponse> {
    return this.api.getChargingSessions(params);
  }

  /**
   * Fetch a single session by ID via OData filter
   */
  async getSessionById(sessionId: number): Promise<ChargeSessionApiModel | null> {
    const response = await this.api.getChargingSessions({
      $filter: `chargeSessionId eq ${sessionId}`,
      $top: 1,
    });
    const rows = response.value as ChargeSessionApiModel[];
    return rows.length > 0 ? rows[0] : null;
  }

  /**
   * Build OData filter string from SessionFilters
   * Matches Flutter's OData filter building pattern
   */
  private buildODataFilter(filters: SessionFilters): string {
    const conditions: string[] = [];

    // Date range filter (always required)
    if (filters.dateFrom && filters.dateTo) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(filters.dateTo);
      toDate.setDate(toDate.getDate() + 1);
      toDate.setHours(0, 0, 0, 0);

      conditions.push(`(deviceLocalStartDate ge ${fromDate.toISOString()})`);

      // Don't filter by stop date if showing live transactions (they have no stop date)
      if (!filters.liveTransactions) {
        conditions.push(`(deviceLocalStopDate lt ${toDate.toISOString()})`);
      }
    }

    // Live transactions filter (active sessions with no stop date)
    if (filters.liveTransactions) {
      conditions.push(`(deviceStopDate eq null)`);
      conditions.push(`(deviceStartDate ne null)`);
    }

    // Session ID (exact match)
    if (filters.sessionId !== undefined) {
      conditions.push(`(chargeSessionId eq ${filters.sessionId})`);
    }

    // Charge Point Name (contains)
    if (filters.chargePointName) {
      conditions.push(`contains(machineName,'${this.escapeOData(filters.chargePointName)}')`);
    }

    // Charge Point Number (contains)
    if (filters.chargePointNumber) {
      conditions.push(`contains(machineOperatorIdentifier,'${this.escapeOData(filters.chargePointNumber)}')`);
    }

    // Connector ID (exact match)
    if (filters.connectorId !== undefined) {
      conditions.push(`(connectorId eq ${filters.connectorId})`);
    }

    // Nayax Transaction ID (exact match)
    if (filters.nayaxTransactionId !== undefined) {
      conditions.push(`(nayaxTransactionId eq ${filters.nayaxTransactionId})`);
    }

    // Account search - HIVE-6307: searches across Account Name, Account ID, and External Code
    if (filters.accountName) {
      const escapedValue = this.escapeOData(filters.accountName);
      // Try numeric match for accountId if the value is a number
      const isNumeric = /^\d+$/.test(filters.accountName);
      if (isNumeric) {
        conditions.push(`(contains(accountName,'${escapedValue}') or accountId eq ${filters.accountName} or contains(externalCode,'${escapedValue}'))`);
      } else {
        conditions.push(`(contains(accountName,'${escapedValue}') or contains(externalCode,'${escapedValue}'))`);
      }
    }

    // Location Usage (multi-select with OR logic)
    if (filters.locationUsage && filters.locationUsage.length > 0) {
      if (filters.locationUsage.length === 1) {
        conditions.push(`(machineUsage eq '${filters.locationUsage[0]}')`);
      } else {
        const usageConditions = filters.locationUsage.map(u => `machineUsage eq '${u}'`).join(' or ');
        conditions.push(`(${usageConditions})`);
      }
    }

    // Charging Station (combined CP Name + CP Number search with OR logic) - HIVE-6245 #6
    if (filters.chargingStation) {
      const escapedValue = this.escapeOData(filters.chargingStation);
      conditions.push(`(contains(machineName,'${escapedValue}') or contains(machineOperatorIdentifier,'${escapedValue}'))`);
    }

    // Physical Reference (contains) - HIVE-6245 #7
    if (filters.physicalReference) {
      conditions.push(`contains(physicalReference,'${this.escapeOData(filters.physicalReference)}')`);
    }

    // Token ID (contains)
    if (filters.tokenId) {
      conditions.push(`contains(idToken,'${this.escapeOData(filters.tokenId)}')`);
    }

    // Payment Method / Card Type (exact match)
    if (filters.paymentMethod) {
      conditions.push(`(cardType eq '${filters.paymentMethod}')`);
    }

    // Session Health filter
    if (filters.sessionHealth === 'active') {
      conditions.push(`(deviceStopDate eq null)`);
    } else if (filters.sessionHealth === 'completed') {
      conditions.push(`(deviceStopDate ne null)`);
    }

    // HIVE-6307: Last 4 Digits filter (credit card)
    if (filters.last4Digits) {
      conditions.push(`(creditCardLast4 eq '${this.escapeOData(filters.last4Digits)}')`);
    }

    return conditions.join(' and ');
  }

  /**
   * Escape special characters in OData string values
   */
  private escapeOData(value: string): string {
    return value.replace(/'/g, "''");
  }

  /**
   * Get charging sessions filtered by SessionFilters
   * Matches Flutter's getChargingSessionsReport method
   */
  async getChargingSessionsByFilters(
    filters: SessionFilters,
    options: { top?: number; skip?: number } = {}
  ): Promise<ChargingSessionsResponse> {
    const odataFilter = this.buildODataFilter(filters);

    const params: ChargingSessionsRequest = {
      $filter: odataFilter,
      $orderby: 'deviceStartDate desc',
    };

    // Actor IDs (custom param, not OData)
    if (filters.actorIds && filters.actorIds.length > 0) {
      params.actorIds = filters.actorIds.join(',');
    }

    // Start Sources (custom param, not OData)
    if (filters.startSources && filters.startSources.length > 0) {
      params.startSources = filters.startSources.join(',');
    }

    // Device Types (custom param, not OData) - HIVE-6618
    if (filters.deviceTypes && filters.deviceTypes.length > 0) {
      params.deviceTypes = filters.deviceTypes.join(',');
    }

    if (options.top !== undefined) {
      params.$top = options.top;
    }

    if (options.skip !== undefined) {
      params.$skip = options.skip;
    }

    return this.api.getChargingSessions(params);
  }

  /**
   * Get total count of charging sessions for filters
   */
  async getChargingSessionsCountByFilters(filters: SessionFilters): Promise<number> {
    const odataFilter = this.buildODataFilter(filters);

    const params: ChargingSessionsRequest = {
      $filter: odataFilter,
    };

    if (filters.actorIds && filters.actorIds.length > 0) {
      params.actorIds = filters.actorIds.join(',');
    }

    if (filters.startSources && filters.startSources.length > 0) {
      params.startSources = filters.startSources.join(',');
    }

    // Device Types (custom param, not OData) - HIVE-6618
    if (filters.deviceTypes && filters.deviceTypes.length > 0) {
      params.deviceTypes = filters.deviceTypes.join(',');
    }

    return this.api.getChargingSessionsCount(params);
  }

  // ---- Legacy methods for backward compatibility ----

  /**
   * Build OData filter for date range
   * @deprecated Use buildODataFilter with SessionFilters instead
   */
  private buildDateRangeFilter(dateFrom: string, dateTo: string): string {
    const fromDate = new Date(dateFrom);
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date(dateTo);
    toDate.setDate(toDate.getDate() + 1);
    toDate.setHours(0, 0, 0, 0);

    return `(deviceLocalStartDate ge ${fromDate.toISOString()}) and (deviceLocalStopDate lt ${toDate.toISOString()})`;
  }

  /**
   * Get charging sessions filtered by date range
   * @deprecated Use getChargingSessionsByFilters instead
   */
  async getChargingSessionsByDateRange(
    dateFrom: string,
    dateTo: string,
    actorIds?: number[],
    options: { top?: number; skip?: number } = {}
  ): Promise<ChargingSessionsResponse> {
    return this.getChargingSessionsByFilters(
      { dateFrom, dateTo, actorIds, groupByLocation: false },
      options
    );
  }

  /**
   * Get total count of charging sessions for date range
   * @deprecated Use getChargingSessionsCountByFilters instead
   */
  async getChargingSessionsCount(
    dateFrom: string,
    dateTo: string,
    actorIds?: number[]
  ): Promise<number> {
    return this.getChargingSessionsCountByFilters({
      dateFrom,
      dateTo,
      actorIds,
      groupByLocation: false,
    });
  }

  /**
   * Get session breakdown for tariff details
   */
  async getSessionBreakdown(machineId: number, sessionId: number): Promise<SessionBreakdownResponse> {
    return this.api.getSessionBreakdown(machineId, sessionId);
  }

  /**
   * Get tariff levels mapping for current operator
   * Returns tariff period names for dynamic column headers
   */
  async getTariffLevels(): Promise<TariffLevelsMappingResponse> {
    return this.api.getTariffLevels();
  }

  /**
   * Get meter values details for a session with pagination
   */
  async getMeterValuesDetails(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<OdataResponse<MeterValueDetailApiModel>> {
    return this.api.getMeterValuesDetails(sessionId, params);
  }

  /**
   * Get meter values count for a session
   */
  async getMeterValuesCount(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<number> {
    return this.api.getMeterValuesCount(sessionId, params);
  }

  /**
   * Export meter values to Excel
   * Returns a blob that can be downloaded
   */
  async exportMeterValuesToExcel(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<Blob> {
    return this.api.getMeterValuesExcel(sessionId, params);
  }
}
